import { useEffect, useState } from 'react';
import { AdminStyles as styles } from '../css/styles';
import { getAdminOverview, getAdminUserGrowth, getAdminLogs, getAdminAiStats } from '../services/api';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const ADMIN_USER_ID = 170;

type Tab = 'users' | 'content' | 'ai' | 'logs';

interface Overview {
    users: {
        total: number;
        teachers: number;
        students: number;
        newThisMonth: number;
        newTeachersMonth: number;
        newStudentsMonth: number;
    };
    courses: { total: number };
    lessons: { total: number };
    groups: { total: number };
    ai: {
        singleTask: { totalRequests: number; avgTextTokens: number; avgImageTokens: number };
        lessonTask: { totalRequests: number; avgTokens: number };
        lessonPlan: { totalRequests: number; avgTokens: number };
    };
}

interface LogEntry {
    timestamp: string;
    message: string;
    error?: string;
    action?: string;
    entity?: string;
    level: string;
}

const StatCard = ({ label, value, sub }: { label: string; value: number; sub?: string }) => (
    <div className={styles.statCard}>
        <span className={styles.statLabel}>{label}</span>
        <span className={styles.statValue}>{value.toLocaleString()}</span>
        {sub && <span className={styles.statSub}>{sub}</span>}
    </div>
);

const TABS: { key: Tab; label: string }[] = [
    { key: 'users', label: 'Users' },
    { key: 'content', label: 'Content' },
    { key: 'ai', label: 'AI Usage' },
    { key: 'logs', label: 'Logs' },
];

const Admin: React.FC = () => {
    const [activeTab, setActiveTab] = useState<Tab>('users');
    const [overview, setOverview] = useState<Overview | null>(null);
    const [growth, setGrowth] = useState<{ month: string; teachers: number; students: number; total: number }[]>([]);
    const [logs, setLogs] = useState<{ errors: LogEntry[]; activity: LogEntry[] } | null>(null);
    const [aiStats, setAiStats] = useState<{ taskType: string; singleText?: { avg: number; count: number }; singleImage?: { avg: number; count: number }; lessonText?: { avg: number; count: number }; lessonImage?: { avg: number; count: number } }[]>([]);
    const [expandedError, setExpandedError] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([getAdminOverview(), getAdminUserGrowth(), getAdminLogs(), getAdminAiStats()])
            .then(([ov, gr, lg, ai]) => {
                setOverview(ov);
                setGrowth(gr);
                setLogs(lg);
                setAiStats(ai);
            })
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className={styles.loading}>Loading...</div>;
    if (!overview) return <div className={styles.loading}>Failed to load data.</div>;

    return (
        <div className={styles.admin}>
            <h1 className={styles.title}>Admin Dashboard</h1>

            <div className={styles.tabs}>
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'users' && (
                <>
                    <div className={styles.section}>
                        <p className={styles.sectionTitle}>Current</p>
                        <div className={styles.statsGrid}>
                            <StatCard label="Total Users" value={overview.users.total} />
                            <StatCard label="Teachers" value={overview.users.teachers} />
                            <StatCard label="Students" value={overview.users.students} />
                        </div>
                    </div>

                    <div className={styles.section}>
                        <p className={styles.sectionTitle}>New (last 30 days)</p>
                        <div className={styles.statsGrid}>
                            <StatCard label="New Users" value={overview.users.newThisMonth} />
                            <StatCard label="New Teachers" value={overview.users.newTeachersMonth} />
                            <StatCard label="New Students" value={overview.users.newStudentsMonth} />
                        </div>
                    </div>

                    <div className={styles.section}>
                        <p className={styles.sectionTitle}>Monthly New Teachers — Last 6 Months</p>
                        <div className={styles.chartWrapper}>
                            <ResponsiveContainer width="100%" height={240}>
                                <LineChart data={growth}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                    <XAxis dataKey="month" tick={{ fill: '#a0aec0', fontSize: 11 }} />
                                    <YAxis tick={{ fill: '#a0aec0', fontSize: 11 }} allowDecimals={false} />
                                    <Tooltip
                                        contentStyle={{ background: '#1a202c', border: 'none', borderRadius: 8, color: 'white' }}
                                        labelStyle={{ color: '#a0aec0' }}
                                    />
                                    <Legend wrapperStyle={{ color: '#a0aec0', fontSize: 12 }} />
                                    <Line type="monotone" dataKey="teachers" stroke="#68d391" strokeWidth={2} dot={false} name="New Teachers" />
                                    <Line type="monotone" dataKey="students" stroke="#63b3ed" strokeWidth={2} dot={false} name="New Students" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'content' && (
                <div className={styles.section}>
                    <div className={styles.statsGrid}>
                        <StatCard label="Courses" value={overview.courses.total} />
                        <StatCard label="Lessons" value={overview.lessons.total} />
                        <StatCard label="Groups" value={overview.groups.total} />
                    </div>
                </div>
            )}

            {activeTab === 'ai' && (
                <>
                    <div className={styles.section}>
                        <p className={styles.sectionTitle}>Single Task Creation</p>
                        <div className={styles.statsGrid}>
                            <StatCard label="Total Requests" value={overview.ai.singleTask?.totalRequests ?? 0} />
                            <StatCard label="Avg Tokens (text)" value={overview.ai.singleTask?.avgTextTokens ?? 0} sub="per request" />
                            <StatCard label="Avg Tokens (image)" value={overview.ai.singleTask?.avgImageTokens ?? 0} sub="per request" />
                        </div>
                    </div>

                    <div className={styles.section}>
                        <p className={styles.sectionTitle}>Lesson Generation — Tasks</p>
                        <div className={styles.statsGrid}>
                            <StatCard label="Total Requests" value={overview.ai.lessonTask?.totalRequests ?? 0} />
                            <StatCard label="Avg Tokens" value={overview.ai.lessonTask?.avgTokens ?? 0} sub="per task" />
                        </div>
                    </div>

                    <div className={styles.section}>
                        <p className={styles.sectionTitle}>Lesson Generation — Planning</p>
                        <div className={styles.statsGrid}>
                            <StatCard label="Plans Generated" value={overview.ai.lessonPlan?.totalRequests ?? 0} />
                            <StatCard label="Avg Tokens" value={overview.ai.lessonPlan?.avgTokens ?? 0} sub="per plan" />
                        </div>
                    </div>

                    <div className={styles.section}>
                        <p className={styles.sectionTitle}>Requests by Task Type</p>
                        <table className={styles.aiTable}>
                            <thead>
                                <tr>
                                    <th>Task Type</th>
                                    <th>Single (text) avg</th>
                                    <th>Single (text) reqs</th>
                                    <th>Single (image) avg</th>
                                    <th>Single (image) reqs</th>
                                    <th>Lesson (text) avg</th>
                                    <th>Lesson (text) reqs</th>
                                    <th>Lesson (image) avg</th>
                                    <th>Lesson (image) reqs</th>
                                </tr>
                            </thead>
                            <tbody>
                                {aiStats.map(row => (
                                    <tr key={row.taskType}>
                                        <td>{row.taskType}</td>
                                        <td>{row.singleText?.avg || '—'}</td>
                                        <td>{row.singleText?.count ?? 0}</td>
                                        <td>{row.singleImage?.avg || '—'}</td>
                                        <td>{row.singleImage?.count ?? 0}</td>
                                        <td>{row.lessonText?.avg || '—'}</td>
                                        <td>{row.lessonText?.count ?? 0}</td>
                                        <td>{row.lessonImage?.avg || '—'}</td>
                                        <td>{row.lessonImage?.count ?? 0}</td>
                                    </tr>
                                ))}
                                {aiStats.length === 0 && (
                                    <tr><td colSpan={9} className={styles.empty}>No data yet</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {activeTab === 'logs' && (
                <div className={styles.section}>
                    <div className={styles.logsGrid}>
                        <div className={styles.logPanel}>
                            <p className={styles.sectionTitle}>Errors</p>
                            {logs?.errors.length === 0 && <p className={styles.empty}>No recent errors</p>}
                            {logs?.errors.map((e, i) => (
                                <div key={i} className={`${styles.logEntry} ${styles.logError}`}>
                                    <div
                                        className={styles.logEntryHeader}
                                        onClick={() => setExpandedError(expandedError === i ? null : i)}
                                    >
                                        <span>{e.error || e.message}</span>
                                        <span className={styles.logExpandIcon}>{expandedError === i ? '▲' : '▼'}</span>
                                    </div>
                                    <div className={styles.logMeta}>{e.timestamp}</div>
                                    {expandedError === i && (
                                        <div className={styles.logDetail}>
                                            {e.message && e.error && <div><span className={styles.logDetailLabel}>Message:</span> {e.message}</div>}
                                            {(e as any).action && <div><span className={styles.logDetailLabel}>Action:</span> {(e as any).action}</div>}
                                            {(e as any).stack && <pre className={styles.logStack}>{(e as any).stack}</pre>}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className={styles.logPanel}>
                            <p className={styles.sectionTitle}>Activity</p>
                            {logs?.activity.length === 0 && <p className={styles.empty}>No recent activity</p>}
                            {logs?.activity.map((e, i) => (
                                <div key={i} className={`${styles.logEntry} ${styles.logInfo}`}>
                                    <div>{e.message}</div>
                                    <div className={styles.logMeta}>{e.action} · {e.entity} · {e.timestamp}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export { ADMIN_USER_ID };
export default Admin;
